import { useState } from 'react'

import { useIsFolderOwner } from 'components/folderPage/folderHooks'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { dropDraggedItem } from 'redux/folders/actions/helpers'
import { SET_FOLDER_STATE } from 'redux/folders/actions/types'

import ToastService from 'services/toast.service'
import folderService from 'services/folder.service'

import { SubfolderFolderItemType } from 'types/folders'

import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import FolderRenameModal from 'components/folderPage/FolderRenameModal'

import styles from './folderOptions.module.scss'

type Props = {
  folder: SubfolderFolderItemType
}

const FolderOptions = ({ folder }: Props) => {
  const isFolderOwner = useIsFolderOwner()

  const [loading, setLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [renameFolder, setRenameFolder] = useState(false)

  const subfolders = useAppSelector(state => state.folders.rootFolder.subfolders)
  const parentFolder = useAppSelector(state => state.folders.rootFolder.parentFolder)

  const [setFolderState] = useActionless(SET_FOLDER_STATE)

  const handleRemoveFolderFromUI = () => {
    const { rootFolder } = store.getState().folders
    const updatedDecks = rootFolder.subfolders.filter(d => d.id !== folder.id)

    setFolderState({
      rootFolder: { ...rootFolder, subfolders: updatedDecks },
    })
  }

  const handleMoveItemToFolder = (folderId: number) => {
    setLoading(true)

    return dropDraggedItem({ type: 'folder', data: { id: folder.id } }, folderId)
      .then(handleRemoveFolderFromUI)
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  const handleTogglePrivate = () => {
    setLoading(true)

    return folderService
      .updateItems([{ id: folder.id, type: 'folder', patch: { private: !folder.private } }])
      .then(() => {
        const { rootFolder } = store.getState().folders
        const updatedDecks = rootFolder.subfolders.map(d => (d.id === folder.id ? { ...d, private: !d.private } : d))

        setFolderState({
          rootFolder: { ...rootFolder, subfolders: updatedDecks },
        })
      })
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  const handleDeleteFolder = () => {
    setLoading(true)

    return folderService
      .deleteItems([{ id: folder.id, type: 'folder' }])
      .then(() => {
        const { rootFolder } = store.getState().folders
        const updatedSubfolders = rootFolder.subfolders.filter(d => d.id !== folder.id)

        setFolderState({
          rootFolder: { ...rootFolder, subfolders: updatedSubfolders },
        })
      })
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  if (!isFolderOwner) return null

  if (loading) return <SimpleSpinner size="xSmall" />

  return (
    <>
      <ArchidektDropdown
        menuClassName={styles.dropdownMenu}
        options={[
          {
            icon: folder.private ? 'unlock' : 'lock',
            label: folder.private ? 'Make public' : 'Make private',
            onClick: handleTogglePrivate,
          },
          { icon: 'edit outline', label: 'Edit folder', onClick: () => setRenameFolder(true) },
          { label: `Delete folder`, onClick: () => setDeleteConfirm(true), icon: 'trash' },
          { type: 'spacer' },
          {
            label: 'Move folder to',
            icon: 'arrow right',
            type: 'extras',
            id: 'move-deck-to-other-dir',
            options: [
              {
                label: 'Parent folder',
                onClick: () => handleMoveItemToFolder(parentFolder.id),
                hidden: !parentFolder?.id,
              },
              { type: 'spacer', hidden: !parentFolder?.id || !subfolders.length },
              ...subfolders.map(subfolder => ({
                label: subfolder.name,
                onClick: () => handleMoveItemToFolder(subfolder.id),
                hidden: subfolder.id === folder.id,
              })),
              // { type: 'spacer', hidden: !parentFolder?.id },
              // { label: 'View full folder tree', onClick: () => null, icon: 'folder open' },
            ],
          },
        ]}
      />

      <ConfirmDeleteModal
        header={`Delete ${folder.name}`}
        delayEnabledTime={3000}
        open={deleteConfirm}
        onConfirm={handleDeleteFolder}
        message={`Are you sure you want to delete the folder "${folder.name}"? This will delete ALL DECKS AND FOLDERS inside ${folder.name}. This action cannot be undone.`}
        onClose={() => setDeleteConfirm(false)}
        loadingMessage="Deleting folder and contents..."
      />

      <FolderRenameModal open={renameFolder} onClose={() => setRenameFolder(false)} folder={folder} />
    </>
  )
}

export default FolderOptions
